import React, { useState, useEffect } from 'react';
import { Typography, Container, Grid, Box, CircularProgress } from '@mui/material';
import MortgagePaymentCalculator from './MortgagePaymentCalculator';
import MortgageSummaryTable from './MortgageSummaryTable';
import PieChart from './PieChart';
import AmortizationGraph from './AmortizationGraph';

const FREQ_LABELS = {
  monthly: 'mensual',
  'semi-monthly': 'quincenal',
  'bi-weekly': 'bisemanal',
  'accelerated-bi-weekly': 'bisemanal acelerado',
  weekly: 'semanal',
  'accelerated-weekly': 'semanal acelerado',
};

const MortgageSummary = () => {
  const [chartData, setChartData] = useState({});
  const [minMaxResults, setMinMaxResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCalculated, setIsCalculated] = useState(false);
  const [isVariableScenario, setIsVariableScenario] = useState(false);

  const initialValues = {
    homePrice: 500000,
    downPayment: 25000,
    interestRate: 5.4,
    loanTerm: 5,
    amortizationPeriod: 25,
    paymentFrequency: 'monthly',
  };

  useEffect(() => {
    handleCalculationStart();
    setTimeout(() => {
      calculateInitialMortgage();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateMinimumDownPayment = (price) => {
    if (price <= 500000) return price * 0.05;
    if (price <= 1499999) return 500000 * 0.05 + (price - 500000) * 0.1;
    return price * 0.2;
  };

  const getMortgageInsuranceRate = (downPaymentPercent) => {
    if (downPaymentPercent >= 20) return 0;
    if (downPaymentPercent >= 15) return 2.8;
    if (downPaymentPercent >= 10) return 3.1;
    if (downPaymentPercent >= 5) return 4.0;
    return 4.0;
  };

  const calculateInitialMortgage = () => {
    const minDownPayment = calculateMinimumDownPayment(initialValues.homePrice);
    const downPayment = Math.max(initialValues.downPayment, minDownPayment);
    const principal = initialValues.homePrice - downPayment;

    let mortgageInsurance = 0;
    const downPaymentPercent = (downPayment / initialValues.homePrice) * 100;
    if (downPaymentPercent < 20) {
      const mortgageInsuranceRate = getMortgageInsuranceRate(downPaymentPercent);
      mortgageInsurance = principal * (mortgageInsuranceRate / 100);
    }

    const totalLoan = principal + mortgageInsurance;
    const annualInterestRate = initialValues.interestRate / 100;
    let paymentsPerYear = 12;
    const nominalRate = annualInterestRate;
    const effectiveAnnualRate = Math.pow(1 + nominalRate / 2, 2) - 1;
    const ratePerPeriod = Math.pow(1 + effectiveAnnualRate, 1 / paymentsPerYear) - 1;
    const totalPayments = paymentsPerYear * initialValues.amortizationPeriod;
    const paymentsInTerm = paymentsPerYear * initialValues.loanTerm;

    const paymentPerPeriod =
      (totalLoan * ratePerPeriod) / (1 - Math.pow(1 + ratePerPeriod, -totalPayments));

    handleCalculationComplete({
      paymentPerPeriod: paymentPerPeriod.toFixed(2),
      principalAmount: principal.toFixed(2),
      totalLoan: totalLoan.toFixed(2),
      numberOfPaymentsAmortization: totalPayments,
      numberOfPaymentsTerm: paymentsInTerm,
      mortgageInsurance: mortgageInsurance.toFixed(2),
      amortizationPeriod: initialValues.amortizationPeriod,
      ratePerPeriod,
      paymentsPerYear,
      totalPayments,
      paymentFrequency: initialValues.paymentFrequency,
      annualInterestRate,
      homePrice: initialValues.homePrice,
      downPayment,
      effectiveAnnualRate,
      isVariable: false,
    });
  };

  const enrichResultWithInterestData = (data) => {
    const pmt = parseFloat(data.paymentPerPeriod);
    const ttlPayments = parseFloat(data.totalPayments);
    const ttlLoan = parseFloat(data.totalLoan);

    if (isNaN(pmt) || isNaN(ttlPayments) || isNaN(ttlLoan)) {
      return { error: 'El cálculo devolvió datos inválidos.' };
    }

    const totalInterestAmortization = pmt * ttlPayments - ttlLoan;
    if (isNaN(totalInterestAmortization)) {
      return { error: 'Cálculo de interés inválido.' };
    }

    let balance = ttlLoan;
    let totalInterestTerm = 0;
    for (let i = 0; i < data.numberOfPaymentsTerm; i++) {
      const interestForPeriod = balance * data.ratePerPeriod;
      totalInterestTerm += interestForPeriod;
      const principalPayment = pmt - interestForPeriod;
      balance -= principalPayment;
    }

    if (isNaN(totalInterestTerm) || isNaN(balance)) {
      return { error: 'Cálculo del plazo inválido.' };
    }

    const totalPrincipalTerm = pmt * data.numberOfPaymentsTerm - totalInterestTerm;
    if (isNaN(totalPrincipalTerm)) {
      return { error: 'Cálculo de capital inválido.' };
    }

    return {
      ...data,
      totalInterestPaidAmortization: totalInterestAmortization.toFixed(2),
      totalInterestPaidTerm: totalInterestTerm.toFixed(2),
      totalCostAmortization: (pmt * ttlPayments).toFixed(2),
      totalCostTerm: (pmt * data.numberOfPaymentsTerm).toFixed(2),
      totalPrincipalPaidTerm: totalPrincipalTerm.toFixed(2),
      remainingBalance: balance.toFixed(2),
    };
  };

  const handleCalculationComplete = (data) => {
    if (data.isVariable) {
      const { minResult, maxResult } = data;
      const enrichedMin = enrichResultWithInterestData(minResult);
      if (enrichedMin.error) { setError(enrichedMin.error); setLoading(false); return; }
      const enrichedMax = enrichResultWithInterestData(maxResult);
      if (enrichedMax.error) { setError(enrichedMax.error); setLoading(false); return; }

      setMinMaxResults({ minResult: enrichedMin, maxResult: enrichedMax });
      setIsVariableScenario(true);
      setIsCalculated(true);
      setLoading(false);
    } else {
      const enriched = enrichResultWithInterestData(data);
      if (enriched.error) { setError(enriched.error); setLoading(false); return; }
      setMinMaxResults(null);
      setIsVariableScenario(false);
      setChartData(enriched);
      setIsCalculated(true);
      setLoading(false);
    }
  };

  const handleCalculationError = (errorMessage) => {
    setError(errorMessage);
    setLoading(false);
  };

  const handleCalculationStart = () => {
    setLoading(true);
    setError('');
  };

  const formatFreq = (freq) => FREQ_LABELS[freq] || freq;

  const renderResultBlock = (data, label) => (
    <Box>
      {label && (
        <Typography variant="h6" sx={{ color: '#1A1A1A', fontWeight: 600, mb: 1 }}>
          {label}: {(data.annualInterestRate * 100).toFixed(1)}%
        </Typography>
      )}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderLeft: '4px solid #1E3A5F',
          borderRadius: '0 14px 14px 0',
          py: 6,
          px: 5,
          boxShadow: '0 1px 8px rgba(11,18,32,0.06)',
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: '14px', color: '#5F6B7A', mb: 1 }}>
          Tu pago {formatFreq(data.paymentFrequency)} será:
        </Typography>
        <Typography
          sx={{
            fontSize: '44px',
            fontWeight: 600,
            color: '#1E3A5F',
            letterSpacing: '-0.5px',
            lineHeight: 1.1,
            fontFeatureSettings: "'tnum'",
          }}
        >
          ${parseFloat(data.paymentPerPeriod).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 2 }}>
        Según tus datos, aquí tienes un resumen de tu hipoteca durante el plazo y el período de amortización.
      </Typography>

      <MortgageSummaryTable data={data} />

      <Typography variant="subtitle1" sx={{ color: '#1A1A1A', fontWeight: 600, mt: 8, mb: 2 }}>
        Desglose del costo total
      </Typography>
      <Box sx={{ width: '100%', height: 280, mb: 2 }}>
        <PieChart
          principalAmount={data.principalAmount}
          totalInterestPaidAmortization={data.totalInterestPaidAmortization}
          mortgageInsurance={data.mortgageInsurance}
        />
      </Box>

      <Typography variant="subtitle1" sx={{ color: '#1A1A1A', fontWeight: 600, mt: 8, mb: 2 }}>
        Capital vs. intereses por año
      </Typography>
      <Box sx={{ width: '100%', height: 300 }}>
        <AmortizationGraph
          totalLoan={data.totalLoan}
          ratePerPeriod={data.ratePerPeriod}
          totalPayments={data.totalPayments}
          paymentPerPeriod={data.paymentPerPeriod}
          paymentsPerYear={data.paymentsPerYear}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: '#F7F9FC', py: 4, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <MortgagePaymentCalculator
              onCalculationComplete={handleCalculationComplete}
              onCalculationError={handleCalculationError}
              onCalculationStart={handleCalculationStart}
              initialValues={initialValues}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress sx={{ color: '#1E3A5F' }} />
              </Box>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : isCalculated ? (
              <>
                {isVariableScenario && minMaxResults ? (
                  <>
                    <Typography variant="h4" sx={{ mt: 2, color: '#1A1A1A', mb: 3 }}>
                      Escenario variable: compara tasas mínima y máxima
                    </Typography>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        {renderResultBlock(minMaxResults.minResult, 'Tasa mínima')}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderResultBlock(minMaxResults.maxResult, 'Tasa máxima')}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  renderResultBlock(chartData, null)
                )}
              </>
            ) : (
              <Typography variant="body1" sx={{ mt: 6, color: '#5F6B7A' }}>
                Completa los datos para ver el resumen de tu hipoteca.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MortgageSummary;
